import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

import KpiCard from "../../../components/common/KpiCard";
import ComplianceCalendar from "../components/ComplianceCalendar";
import Placeholder from "../../../components/common/Placeholder";
import CalendarTab from "../components/CalendarTab";
import {
  HiOutlineChartBar,
  HiOutlineCalendar,
  HiOutlineClipboardList,
  HiOutlineCurrencyRupee,
  HiOutlineDocumentText,
  HiOutlineClock
} from "react-icons/hi";

import AssignmentDocumentsTab from "../components/AssignmentDocumentsTab";



const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const tabs = [
  { key: "Overview", label: "Overview", icon: HiOutlineChartBar },
  { key: "Calendar", label: "Calendar", icon: HiOutlineCalendar },
  { key: "Scope", label: "Scope", icon: HiOutlineClipboardList },
  { key: "Commercial", label: "Commercial", icon: HiOutlineCurrencyRupee },
  { key: "Documents", label: "Documents", icon: HiOutlineDocumentText },
  { key: "Activity", label: "Activity", icon: HiOutlineClock }
];


const EngagementDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("Overview");
  const [engagement, setEngagement] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadEngagement();
  }, [id]);

  const loadEngagement = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${API_URL}/api/assignments/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setEngagement(res.data.assignment);
      setLineItems(res.data.lineItems || []);
      
    } catch (err) {
      console.error("Error loading engagement:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-slate-500">
        Loading engagement details...
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="p-6 text-red-500">
        Engagement not found.
      </div>
    );
  }

  const calculateLineItemValue = (item) => {
  const fee = Number(item.FeeAmount || 0);

  switch (item.RecurrenceType) {
    case "Monthly":
      return fee * 12;
    case "Quarterly":
      return fee * 4;
    case "Annual":
      return fee;
    case "OneTime":
      return fee;
    default:
      return fee;
  }
};

const totalContractValue = lineItems.reduce(
  (sum, item) => sum + calculateLineItemValue(item),
  0
);


  return (
    <div>

      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-start mb-6">

        <div>
          <h1 className="text-xl font-semibold">
            {engagement.AssignmentName}
          </h1>

          <p className="text-sm text-slate-500">
            {engagement.ClientName} • {engagement.FinancialYear}
          </p>

          <p className="text-sm mt-2">
            Status: {engagement.Status}
          </p>
        </div>

        <button
          onClick={() => navigate(`/assignments/edit/${id}`)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-indigo-700 transition"
        >
          Edit
        </button>

      </div>

      {/* ================= KPI CARDS ================= */}
      <div className="grid grid-cols-4 gap-4 mb-6">

        <KpiCard
          label="Financial Year"
          value={engagement.FinancialYear}
        />

        <KpiCard
          label="Start Date"
          value={engagement.StartDate?.split("T")[0]}
        />

        <KpiCard
          label="End Date"
          value={engagement.EndDate?.split("T")[0] || "-"}
        />

        <KpiCard
          label="Status"
          value={engagement.Status}
        />

      </div>

      {/* ================= TABS ================= */}
<div className="bg-white border rounded-xl px-6 pt-4 border-slate-200 mb-6">

  <div className="flex gap-8 text-sm font-semibold border-b border-slate-200">

    {tabs.map(tab => {
      const Icon = tab.icon;

      return (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          className={`pb-3 flex items-center gap-2 transition ${
            activeTab === tab.key
              ? "border-b-2 border-indigo-600 text-indigo-600"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Icon className="w-4 h-4" />
          {tab.label}
        </button>
      );
    })}

  </div>

</div>


      {/* ================= TAB CONTENT ================= */}

      {/* OVERVIEW */}
      {activeTab === "Overview" && (
        <div className="bg-white p-6 rounded-lg border space-y-4">

          <div>
            <p className="text-sm text-slate-400">Scope</p>
            <p className="text-slate-700">
              {engagement.Scope || "No scope defined."}
            </p>
          </div>

          <div>
            <p className="text-sm text-slate-400">Deliverables</p>
            <p className="text-slate-700">
              {engagement.Deliverables || "No deliverables defined."}
            </p>
          </div>

        </div>
      )}

      {/* CALENDAR */}
{activeTab === "Calendar" && (
  <CalendarTab assignmentId={id} />
)}


      {/* SCOPE */}
      {activeTab === "Scope" && (
        <div className="bg-white p-6 rounded-lg border">
          {engagement.Scope || "No scope defined."}
        </div>
      )}

      {/* COMMERCIAL */}
{activeTab === "Commercial" && (
  <div className="bg-white border rounded-xl overflow-hidden">
    <table className="w-full text-sm">

      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="text-left px-4 py-3">Service</th>
          <th className="text-left px-4 py-3">Recurrence</th>
          <th className="text-left px-4 py-3">Start Date</th>
          <th className="text-left px-4 py-3">End Date</th>
          <th className="text-right px-4 py-3">Fee</th>
          <th className="text-left px-4 py-3">Billing</th>
        </tr>
      </thead>

      <tbody>
        {lineItems.map(item => (
          <tr key={item.LineItemID} className="border-t">
            <td className="px-4 py-3">{item.ServiceName}</td>
            <td className="px-4 py-3">{item.RecurrenceType}</td>
            <td className="px-4 py-3">
              {item.StartDate?.split("T")[0]}
            </td>
            <td className="px-4 py-3">
              {item.EndDate?.split("T")[0] || "-"}
            </td>
            <td className="px-4 py-3 text-right font-medium">
              ₹ {Number(item.FeeAmount).toLocaleString()}
            </td>
            <td className="px-4 py-3">{item.BillingFrequency}</td>
          </tr>
        ))}
      </tbody>

      <tfoot className="bg-slate-50 border-t">
        <tr>
          <td colSpan="4" className="px-4 py-4 text-right font-semibold text-slate-700">
            Total Contract Value
          </td>
          <td className="px-4 py-4 text-right font-bold text-indigo-700 text-base">
            ₹ {lineItems
                .reduce((sum, item) => sum + Number(item.FeeAmount || 0), 0)
                .toLocaleString()}
          </td>
          <td></td>
        </tr>
      </tfoot>

    </table>
  </div>
)}
      {/* DOCUMENTS */}
      {activeTab === "Documents" && (
  <AssignmentDocumentsTab assignmentId={id} />
)}


      {/* ACTIVITY */}
      {activeTab === "Activity" && (
        <Placeholder title="Activity Log (To be implemented)" />
      )}

      </div>

    
  );
};

export default EngagementDetail;
